import ArticleCardList from "components/general/ArticleCardList";
import Container from "components/general/Container";
import PageHeader from "components/general/Headers/PageHeader";
import ScrollContainer from "components/general/ScrollContainer";
import Section from "components/general/Section";
import Seo from "components/general/Seo";
import { Box, H2, HStack, Typography } from "components/ui";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { ActivityIndicator } from "react-native";
import { useGetArticleQuery } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
// import {LineChart} from "react-gifted-charts";

export default function HomePage() {
	const [pageNumber, setPageNumber] = React.useState(1);
	const [allArticles, setAllArticles] = React.useState<any[]>([]);
	const [isLoadingMore, setIsLoadingMore] = React.useState(false);
	const loadMoreBoxRef = useRef<any>(null);
	const isMobile = useIsMobile();

	const {
		data,
		isLoading: pageIsLoading,
		isFetching: pageIsFetching,
	} = useGetArticleQuery({
		pageSize: 24,
		pageNumber: pageNumber,
	});

	React.useEffect(() => {
		if (data?.list && !pageIsFetching) {
			if (pageNumber === 1) {
				setAllArticles(data.list);
			} else {
				setAllArticles((prev) => [...prev, ...(data.list || [])]);
			}
			setIsLoadingMore(false);
		}
	}, [data, pageNumber, pageIsFetching]);

	const hasMoreItems = useMemo(() => {
		const hasMore = data?.metaData && allArticles.length > 0 && allArticles.length < data.metaData.totalItemCount;
		return hasMore;
	}, [data?.metaData, allArticles.length]);

	const handleLoadMore = useCallback(() => {
		if (!isLoadingMore && hasMoreItems) {
			setIsLoadingMore(true);
			setPageNumber((prev) => prev + 1);
		}
	}, [isLoadingMore, hasMoreItems, pageNumber]);

	useEffect(() => {
		if (!loadMoreBoxRef.current || !hasMoreItems) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (entry.isIntersecting && !isLoadingMore) {
					handleLoadMore();
				}
			},
			{
				threshold: 0.1, // Trigger when 10% of the element is visible
				rootMargin: "100px", // Trigger 100px before the element is fully visible
			},
		);

		observer.observe(loadMoreBoxRef.current);

		return () => {
			if (loadMoreBoxRef.current) {
				observer.unobserve(loadMoreBoxRef.current);
			}
			observer.disconnect(); // Properly disconnect the observer
		};
	}, [handleLoadMore, hasMoreItems, isLoadingMore]);

	if (pageIsLoading && pageNumber === 1) {
		return (
			<Box height={"$full"} justifyContent="center" alignItems="center" bg="transparent">
				<Box flex={1} justifyContent="center">
					<ActivityIndicator size="large" style={{ transform: [{ scaleX: 2 }, { scaleY: 2 }] }} />
				</Box>
			</Box>
		);
	}

	return (
		<ScrollContainer>
			<Seo
				title={"Cleaning & Hygiene Insights | Abco Articles & Resources"}
				description={"Discover expert articles on cleaning, hygiene, sustainability, and equipment. Stay informed with tips, guides, and industry insights from Abco."}
			/>
			<Container>
				<PageHeader
					title="Articles"
					description={
						"Explore a curated collection of articles designed to inform, inspire, and empower. From industry insights and practical how-to guides to thought leadership and trend analysis, our content is crafted to help you stay ahead, make smarter decisions, and spark new ideas in your field.Discover expert insights, practical tips, and industry updates in our curated collection of articles — all designed with cleaning professionals, business owners, and facility managers in mind. Whether you're looking to improve efficiency, ensure compliance, or stay ahead of the latest innovations in cleaning products and hygiene solutions, our content offers real-world value. From product guides and safety best practices to sustainability trends and cost-saving strategies, each article is crafted to help you make informed decisions, maintain high standards, and build a cleaner, healthier environment."
					}
				/>
				<Box gap={12}>
					<Section gap={24}>
						<Box justifyContent="center" alignItems="center">
							<H2>Browse articles</H2>
						</Box>
						{allArticles && (
							<>
								<ArticleCardList list={allArticles} scrollEnabled={false} />
								{hasMoreItems && (
									<Box ref={loadMoreBoxRef} bg="$transparent" height={100} justifyContent="center" alignItems="center">
										{isLoadingMore && (
											<HStack space="md" alignItems="center">
												<ActivityIndicator size="large" />
												<Typography color="$primary" weight="bold">
													Loading more articles...
												</Typography>
											</HStack>
										)}
									</Box>
								)}
							</>
						)}
					</Section>
				</Box>
			</Container>
		</ScrollContainer>
	);
}
